export type CookieCategory = "necessary" | "analytics" | "marketing"

export type CookieConsentPreferences = Record<CookieCategory, boolean>

export type CookieConsentChoice = "acceptAll" | "rejectAll" | "custom"

export interface CookieConsentRecord {
  version: number
  choice: CookieConsentChoice
  preferences: CookieConsentPreferences
  updatedAt: string
  // saved while the browser privacy signal was on, so it overrides the signal
  globalPrivacyControl?: boolean
}

// the consent in effect when an order is placed
export interface CookieConsentSnapshot {
  choice: CookieConsentChoice | null
  preferences: CookieConsentPreferences
  updatedAt: string | null
  version: number
  globalPrivacyControl: boolean
}

// bump to invalidate every stored consent and prompt visitors again
export const CONSENT_VERSION = 1

export const DEFAULT_STORAGE_KEY = "cookie_consent"

// available categories to consent to
export const COOKIE_CATEGORIES: CookieCategory[] = [
  "necessary",
  "analytics",
  "marketing",
]

export const COOKIE_CONSENT_CHOICES: CookieConsentChoice[] = [
  "acceptAll",
  "rejectAll",
  "custom",
]

// presets below: analytics maps to GTM analytics_storage, marketing maps to
// ad_storage, ad_user_data and ad_personalization, necessary is always on

// starting position of the "Customize" switches when nothing is saved yet,
// the visitor still has to press Save for it to apply
export const COOKIE_CONSENT_DEFAULT_PREFERENCES: CookieConsentPreferences = {
  necessary: true,
  analytics: true,
  marketing: true,
}

// what applies before the visitor makes a choice: sent to GTM as the consent
// default, in the cookie_consent_update event and in the order metadata
export const COOKIE_CONSENT_INITIAL_PREFERENCES: CookieConsentPreferences = {
  necessary: true,
  analytics: true,
  marketing: true,
}

// what applies under the browser privacy signal before the visitor makes a
// choice with the signal on: everything optional off, also used as the
// starting position of the "Customize" switches and to override choices
// saved before the signal was turned on
export const COOKIE_CONSENT_PRIVACY_SIGNAL_PREFERENCES: CookieConsentPreferences =
  {
    necessary: true,
    analytics: false,
    marketing: false,
  }

// saved by "Opt out", or "Keep opted out" under the browser privacy signal
export const COOKIE_CONSENT_REJECT_ALL: CookieConsentPreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
}

// saved by "Continue", or "Allow sharing" under the browser privacy signal
export const COOKIE_CONSENT_ACCEPT_ALL: CookieConsentPreferences = {
  necessary: true,
  analytics: true,
  marketing: true,
}
